import { CircleDollarSign, Scale, Users, Eye, Check, X, BadgeCheck, Quote, ShieldCheck } from "lucide-react";

const CARDS = [
  { icon: CircleDollarSign, title: "Zero Project Commission", desc: "We never take a cut of your construction project — no matter how big your build or which partner you choose." },
  { icon: Scale, title: "Independent Advice", desc: "Every recommendation comes from NCIS™, engineering principles and your requirements — never from vendor payments." },
  { icon: Users, title: "You Choose Your Partners", desc: "We surface verified, qualified professionals. The final decision is always yours, with no pressure and no lock-in." },
  { icon: Eye, title: "Complete Transparency", desc: "Clear costs, honest assessments and no hidden incentives. You see exactly what drives every number and suggestion." },
];

const COMPARE = [
  { label: "Commission on your project", trad: "Yes — often 5–10%", ours: "₹0 — Never" },
  { label: "Whose side are they on?", trad: "The contractor's", ours: "Yours, always" },
  { label: "How partners are recommended", trad: "Whoever pays the most", ours: "NCIS™ + your needs" },
  { label: "Choice of contractor", trad: "Limited to paid listings", ours: "You choose, freely" },
  { label: "Pricing & advice", trad: "Opaque, incentivised", ours: "Fully transparent" },
];

const BADGES = [
  "Zero Project Commission", "Zero Hidden Charges", "Independent Construction Intelligence",
  "AI + Human Expert Guidance", "Transparent Decision Support",
];

export default function OurPromise() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-20 lg:py-28" data-testid="our-promise-section">
      {/* Header */}
      <div className="max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-wider text-[#0E9AC8] animate-fade-up">Our Promise</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold text-slate-900 animate-fade-up" style={{ animationDelay: "60ms" }}>
          100% Independent. Zero Commission.<br className="hidden sm:block" /> Zero Hidden Incentives.
        </h2>
        <p className="mt-5 text-lg font-medium text-slate-700 animate-fade-up" style={{ animationDelay: "120ms" }}>
          Nirmantra.ai works for property owners — not contractors.
        </p>
        <p className="mt-3 text-slate-600 leading-relaxed animate-fade-up" style={{ animationDelay: "160ms" }}>
          We never charge a commission on construction projects awarded through the platform. Every recommendation is
          driven by NCIS™, engineering principles, your project requirements and your needs — never by vendor payments.
          That independence is the foundation of every insight we give you.
        </p>
      </div>

      {/* Feature cards */}
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="promise-cards">
        {CARDS.map((c, i) => (
          <div key={c.title} data-testid={`promise-card-${i}`}
            className="group card-premium rounded-2xl p-6 animate-fade-up transition-all duration-300 hover:-translate-y-1.5 hover:border-[#0E9AC8]/60 hover:shadow-[0_18px_40px_-24px_rgba(14,154,200,0.55)]"
            style={{ animationDelay: `${i * 70}ms` }}>
            <div className="h-12 w-12 rounded-xl bg-[#0E9AC8]/10 text-[#0E9AC8] flex items-center justify-center transition-colors group-hover:bg-[#0E9AC8] group-hover:text-white">
              <c.icon className="h-6 w-6" strokeWidth={1.6} />
            </div>
            <h3 className="mt-5 font-display text-lg font-extrabold text-slate-900">{c.title}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
          </div>
        ))}
      </div>

      {/* Comparison table */}
      <div className="mt-14 card-premium rounded-2xl overflow-hidden" data-testid="promise-compare">
        <div className="grid grid-cols-[1.4fr_1fr_1fr]">
          <div className="px-5 py-4 bg-slate-900 text-white text-sm font-semibold">How we compare</div>
          <div className="px-4 py-4 bg-slate-800 text-slate-300 text-sm font-semibold text-center">Traditional Platforms</div>
          <div className="px-4 py-4 bg-[#0E9AC8] text-white text-sm font-semibold text-center flex items-center justify-center gap-1.5">
            <ShieldCheck className="h-4 w-4" /> Nirmantra.ai
          </div>
        </div>
        {COMPARE.map((r, i) => (
          <div key={r.label} data-testid={`promise-compare-row-${i}`}
            className={`grid grid-cols-[1.4fr_1fr_1fr] items-center border-t border-slate-100 ${i % 2 ? "bg-slate-50/60" : "bg-white"}`}>
            <div className="px-5 py-4 text-sm font-medium text-slate-800">{r.label}</div>
            <div className="px-4 py-4 flex items-center justify-center gap-2 text-sm text-slate-500 text-center">
              <X className="h-4 w-4 text-slate-300 shrink-0" strokeWidth={2.5} /> <span>{r.trad}</span>
            </div>
            <div className="px-4 py-4 flex items-center justify-center gap-2 text-sm font-semibold text-slate-800 text-center bg-[#0E9AC8]/5">
              <Check className="h-4 w-4 text-[#0E9AC8] shrink-0" strokeWidth={3} /> <span>{r.ours}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Highlighted banner */}
      <div className="mt-12 relative overflow-hidden rounded-3xl bg-[#0B1B2B] text-white px-8 py-12 sm:px-14 sm:py-14" data-testid="promise-banner">
        <div className="absolute inset-0 ncis-grid-bg opacity-50" />
        <div className="absolute -top-16 -right-10 h-64 w-64 rounded-full bg-[#0E9AC8]/25 blur-[110px]" />
        <div className="relative flex items-start gap-5">
          <Quote className="h-10 w-10 text-[#4FD1DB] shrink-0 hidden sm:block" strokeWidth={1.5} />
          <p className="font-display text-2xl sm:text-3xl font-extrabold leading-snug tracking-tight">
            Our business grows when we help you make better construction decisions — not when your project becomes more expensive.
          </p>
        </div>
      </div>

      {/* Trust badges */}
      <div className="mt-10 flex flex-wrap justify-center gap-3" data-testid="promise-badges">
        {BADGES.map((b, i) => (
          <span key={b} data-testid={`promise-badge-${i}`}
            className="inline-flex items-center gap-2 rounded-full border border-[#0E9AC8]/25 bg-[#0E9AC8]/5 px-4 py-2 text-sm font-medium text-slate-700 animate-fade-up"
            style={{ animationDelay: `${i * 60}ms` }}>
            <BadgeCheck className="h-4 w-4 text-[#0E9AC8]" strokeWidth={2} /> {b}
          </span>
        ))}
      </div>
    </section>
  );
}
